import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Navigate } from "react-router-dom";
import { Avatar } from "../../components/Avatar.js";
import { Notice } from "../../components/ui.js";
import { hasRole, useAuth } from "../../lib/auth.js";
import {
  fetchEventPayments,
  setRegistrationPaid,
  type EntryPayment,
} from "../../lib/tournaments.js";
import type { TournamentEvent } from "../../lib/types.js";
import { EventGate, EventScreen } from "./shared.js";

/**
 * Who has paid the registration amount, for an organiser to tick off.
 *
 * Bookkeeping, not a payment screen: no amount, and only the registered are listed, because
 * nobody on the waitlist has been asked for money yet. The database answers nobody but an
 * organiser, and this redirects everyone else to the event rather than showing an empty list.
 */
export function EventPayments() {
  return <EventGate>{({ event }) => <Payments event={event} />}</EventGate>;
}

function Payments({ event }: { event: TournamentEvent }) {
  const { member } = useAuth();
  const queryClient = useQueryClient();
  const key = ["event-payments", event.id];
  const organiser = hasRole(member, "organiser");

  const { data: entries = [], isPending } = useQuery({
    queryKey: key,
    queryFn: () => fetchEventPayments(event.id),
    enabled: organiser,
  });

  const toggle = useMutation({
    mutationFn: (entry: EntryPayment) => setRegistrationPaid(event.id, entry.memberId, !entry.paid),
    // Tick at once and put it back if the database says no, so a tap on a hall's wifi does
    // not feel like nothing happened.
    onMutate: (entry) => {
      const before = queryClient.getQueryData<EntryPayment[]>(key);
      queryClient.setQueryData<EntryPayment[]>(key, (rows = []) =>
        rows.map((row) => (row.memberId === entry.memberId ? { ...row, paid: !row.paid } : row)),
      );
      // After the flip, so the box changes in the same tick as the tap; this only stops a
      // refetch already in flight from landing on top of it.
      void queryClient.cancelQueries({ queryKey: key });
      return { before };
    },
    onError: (_error, _entry, context) => queryClient.setQueryData(key, context?.before),
    onSettled: () => queryClient.invalidateQueries({ queryKey: key }),
  });

  if (!organiser) return <Navigate to={`/events/${event.id}`} replace />;

  const paid = entries.filter((entry) => entry.paid).length;

  return (
    <EventScreen event={event} title="Payments">
      {isPending ? (
        <p className="micro">Loading…</p>
      ) : entries.length === 0 ? (
        <p className="micro">Nobody is registered yet.</p>
      ) : (
        <>
          <p className="micro mb-3" data-testid="payments-count">
            {paid} of {entries.length} paid
          </p>
          <ul>
            {entries.map((entry) => (
              <li
                key={entry.memberId}
                className="border-b"
                style={{ borderColor: "var(--color-line-muted)" }}
              >
                <label className="flex items-center gap-3" style={{ minHeight: 56 }}>
                  <input
                    type="checkbox"
                    checked={entry.paid}
                    onChange={() => toggle.mutate(entry)}
                    className="h-5 w-5 shrink-0"
                  />
                  <Avatar name={entry.displayName} url={entry.avatarUrl} size={26} />
                  <span className="flex-1 text-base font-semibold">{entry.displayName}</span>
                </label>
              </li>
            ))}
          </ul>
        </>
      )}
      {toggle.isError ? <Notice tone="attention">{(toggle.error as Error).message}</Notice> : null}
    </EventScreen>
  );
}
